// Copyright (c) Ping. All rights reserved.
//
// One-tour-at-a-time coordinator. Several first-run guides can want the
// screen at once (the app intro tour, the recovery-phrase nudge, the
// settings walkthrough), and two full-screen spotlights on top of each
// other is a mess. Every guide `request`s a slot with a priority; the
// coordinator keeps exactly one active and queues the rest, promoting the
// highest-priority waiter when the active one `release`s.
//
// Deliberately simple: no preemption (an open guide is never interrupted),
// so priority only decides ordering among guides waiting together. That is
// the right trade for onboarding, where finishing what the user is looking
// at beats reshuffling.

import { create } from 'zustand';

interface PendingRequest {
  readonly id: string;
  readonly priority: number;
}

interface OnboardingCoordinatorState {
  /** The guide currently allowed to show, or null when the screen is free. */
  readonly active: string | null;
  readonly queue: readonly PendingRequest[];
  /** Ask for the screen. Idempotent per id. */
  request(id: string, priority: number): void;
  /** Give up the screen (or leave the queue). Promotes the next waiter. */
  release(id: string): void;
}

/** Pick the highest-priority waiter (ties resolve to whoever queued first). */
function promote(
  active: string | null,
  queue: readonly PendingRequest[],
): Pick<OnboardingCoordinatorState, 'active' | 'queue'> {
  if (active !== null || queue.length === 0) {
    return { active, queue };
  }
  let best: PendingRequest | undefined;
  for (const req of queue) {
    if (best === undefined || req.priority > best.priority) {
      best = req;
    }
  }
  if (best === undefined) {
    return { active, queue };
  }
  const winner = best;
  return { active: winner.id, queue: queue.filter((req) => req.id !== winner.id) };
}

export const useOnboardingCoordinator = create<OnboardingCoordinatorState>((set) => ({
  active: null,
  queue: [],
  request: (id, priority) =>
    set((state) => {
      if (state.active === id || state.queue.some((req) => req.id === id)) {
        return state;
      }
      return promote(state.active, [...state.queue, { id, priority }]);
    }),
  release: (id) =>
    set((state) => {
      const queue = state.queue.filter((req) => req.id !== id);
      const active = state.active === id ? null : state.active;
      return promote(active, queue);
    }),
}));
